import { action, makeAutoObservable, runInAction, toJS } from 'mobx'
import { decorate, inject, injectable } from 'inversify'
import { SessionService } from '@evertel/session/SessionService'
import { Api } from '@evertel/api'
import { APIDataReaction, APIDataRoomMessage, APIDataThreadMessage } from '@evertel/types'
import { uniqBy } from 'lodash'


class MessageReactionsController {
    message: APIDataRoomMessage | APIDataThreadMessage = {}
    modelType: 'room' | 'thread' = 'room'
    reactions = []
    reactionsCount = 0
    reactedBy = []
    reactedByCount = 0

    constructor(
        private session: SessionService,
        private api: Api
    ) {
        makeAutoObservable(this, {init: action})
    }

    init = (message: APIDataRoomMessage | APIDataThreadMessage, modelType: 'room' | 'thread') => {
        this.message = message
        this.modelType = modelType
    }

    toggleReaction = async (emojiText: string): Promise<'added' | 'removed'> => {
        const existingReaction = this.message.reactions?.find(r => r.text === emojiText)
        const hasUserReacted = existingReaction?.reactedByThrough?.some(
            rb => rb.blueUserId === this.session.currentUserId
        ) ?? false

        if (hasUserReacted) {
            await this.removeReaction(emojiText)
            return 'removed'
        } else {
            await this.addReaction(emojiText)
            return 'added'
        }
    }

    addReaction = async (text: string) => {
        try {
            const endpoint = this.modelType === 'room'
                ? this.api.Routes.RoomMessage
                : this.api.Routes.ThreadMessage

            const updatedReactionData = await endpoint.putReactions(this.message.id, text)
            runInAction(() => {
                this.message.reactions = uniqBy([updatedReactionData, ...this.message.reactions], 'id')
            })
        } catch (e) {
            throw new Error(e.message)
        }
    }

    removeReaction = async (text: string) => {
        const existingReaction = this.message.reactions.find(r => r.text === text)
        const isMyReaction = existingReaction.reactedByThrough?.find(rb => rb.blueUserId === this.session.currentUserId)
        const isTheLastReaction = isMyReaction && existingReaction?.count === 1

        // we have the last reaction, remove it before we make the API call
        if (isTheLastReaction) {
            runInAction(() => {
                this.message.reactions = this.message.reactions.filter(r => r.text !== text)
            })
        }

        try {
            const endpoint = this.modelType === 'room'
                ? this.api.Routes.RoomMessage
                : this.api.Routes.ThreadMessage

            const updatedReactionData = await endpoint.delReactions(this.message.id, text)

            // only add back if new count is greater than zero
            if (updatedReactionData.count) {
                runInAction(() => {
                    this.message.reactions = uniqBy([updatedReactionData, ...this.message.reactions], 'id')
                })
            }
        } catch (e) {
            throw new Error(e.message)
        }
    }

    clearReactedBy() {
        this.reactedBy = []
    }
}

decorate(injectable(), MessageReactionsController)
decorate(inject(SessionService), MessageReactionsController, 0)
decorate(inject(Api), MessageReactionsController, 1)

export { MessageReactionsController }
